import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  MapContainer,
  useMapEvents,
  TileLayer,
  Marker,
  Polyline,
  ImageOverlay,
  Tooltip,
  Polygon,
  Popup,
} from "react-leaflet";
import { Alert } from "@mui/material";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { ReactComponent as DestIcon } from "../../assets/icons/general/destIcon.svg";
import { ReactComponent as PoiWrapper } from "../../assets/icons/general/poi_wrapper2.svg";
import { ReactComponent as Delete } from "../../assets/icons/toursManagement/delete.svg";
import { ReactComponent as Move } from "../../assets/icons/toursManagement/move.svg";
import { ReactComponent as Insert } from "../../assets/icons/toursManagement/insert.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/toursManagement/clock.svg";
import { ReactComponent as SmsIcon } from "../../assets/icons/toursManagement/sms.svg";
import { ReactComponent as SoundIcon } from "../../assets/icons/toursManagement/sound.svg";
import ChipStack from './ChipStack'; 

import { MapRef } from "./MapRef";
import Path from "./path";
import useAuth from "../../hooks/Auth/useAuth";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import { convertPathPoitToLatLng } from "../../utils/helperFunctions";
import "leaflet-easybutton/src/easy-button.css";
import MemomizedTaskIcon from "./tasks";
import UseMapClickEvent from "./useMapClickEvent";
import Road from "./Road"; 

import "leaflet-geotiff";
import "leaflet-geotiff/leaflet-geotiff-plotty";
import "leaflet-geotiff/leaflet-geotiff-vector-arrows";
import "leaflet-easybutton/src/easy-button.js";
import "font-awesome/css/font-awesome.min.css";
import "../../styles/leafletMapStyles.css";
import AddPoiMarker from "./addPoiMarker";
import RoadRecordPath from "./RoadRecordPath";

// Stable fallback coords to avoid infinite re-render loops when `mapData` is missing
const FALLBACK_DEFAULT_POINT = Object.freeze([32.0853, 34.7818]); // Tel Aviv
const FALLBACK_MARGIN = 0.01;

const sameLatLng = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length >= 2 &&
  b.length >= 2 &&
  Number(a[0]) === Number(b[0]) &&
  Number(a[1]) === Number(b[1]);

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toLatLngPair = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    const lat = toFiniteNumber(value[0]);
    const lng = toFiniteNumber(value[1]);
    if (lat == null || lng == null) return null;
    return [lat, lng];
  }
  if (value && typeof value === "object") {
    const lat = toFiniteNumber(value.lat);
    const lng = toFiniteNumber(value.lng);
    if (lat == null || lng == null) return null;
    return [lat, lng];
  }
  return null;
};

const parseMemoPosition = (memoLocation) => {
  if (typeof memoLocation !== "string") return null;
  try {
    const parsed = JSON.parse(memoLocation);
    if (!Array.isArray(parsed?.coordinates) || parsed.coordinates.length < 2) return null;
    const lng = toFiniteNumber(parsed.coordinates[0]);
    const lat = toFiniteNumber(parsed.coordinates[1]);
    if (lat == null || lng == null) return null;
    return [lat, lng];
  } catch {
    return null;
  }
};

const isValidLatLngObject = (value) => {
  if (!value || typeof value !== "object") return false;
  const lat = toFiniteNumber(value.lat);
  const lng = toFiniteNumber(value.lng);
  if (lat == null || lng == null) return false;
  return true;
};

const isValidPolylinePositions = (positions) =>
  Array.isArray(positions) && positions.length >= 2;

const isValidPolygonPositions = (positions) =>
  Array.isArray(positions) && positions.length >= 3;

//ICONS
const POI_yellow_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "#FFD700" }}>
    <PoiWrapper />
  </span>
);
const POI_green_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "#0DD02C" }}>
    <PoiWrapper />
  </span>
);
const POI_white_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "white" }}>
    <PoiWrapper />
  </span>
);
const POI_blue_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "#009ddb" }}>
    <PoiWrapper />
  </span>
);
const POI_red_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "#FF3239" }}>
    <PoiWrapper />
  </span>
);
const POI_gray_wrapper_Icon = renderToStaticMarkup(
  <span style={{ color: "#A9A9A9" }}>
    <PoiWrapper />
  </span>
);
const myCustomColour = "#583470";

const convertWKTToLatLngArray = (wktString) => {
  if (typeof wktString !== "string") return null;

  let latLngArray = [];
  if (wktString.startsWith('POLYGON Z')) {
    const regex = /POLYGON Z \(\(([^)]+)\)\)/;
    const match = wktString.match(regex);
    if (match) {
      const points = match[1].split(',').map(point => point.trim().split(' ').map(Number));
      latLngArray = points.map(point => [point[1], point[0]]); // Reverse to [lat, lng]
    }
  }
  return latLngArray;
};


const generateIconHtmlContent = (
  p_text,
  taskIconsMarkup = "",
  color = "blue"
) => {
  return `
      <div style="display:flex;justify-content:center;align-items:center;position:relative;bottom:25px;">
          <div style="position:absolute;">${color === "blue" ? POI_blue_wrapper_Icon : (color === "red" ? POI_red_wrapper_Icon : POI_gray_wrapper_Icon)
    }</div>
          <div style="position:relative;bottom:6px;height:25px;width:35px;display:flex;justify-content:center;align-items:center;font-size:16px;color:black;">
              <b>${p_text}</b>
          </div>
          <div style="
              position:absolute; 
              top:-50px; 
              left:50%; 
              transform: translateX(-50%);
              display:flex; 
              justify-content:center; 
              align-items:center;
          ">
              ${taskIconsMarkup}
          </div>
      </div>`;
};

const destinationIcon = renderToStaticMarkup(<DestIcon />);

// Haversine distance in meters between two {lat,lng}
const haversineMeters = (a, b) => {
  if (!a || !b || a.lat == null || a.lng == null || b.lat == null || b.lng == null) return Infinity;
  const R = 6371000; // meters
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
};

function formatedTrackPath(arr) {
  if (!Array.isArray(arr)) return [];
  const resTrack = [];
  arr.forEach((pathObj) => {
    if (!Array.isArray(pathObj) || pathObj.length < 2) return;
    const lng = toFiniteNumber(pathObj[0]);
    const lat = toFiniteNumber(pathObj[1]);
    if (lat == null || lng == null) return;
    resTrack.push([lat, lng]);
  });
  return resTrack;
}

const convertPOiLocationStringToMarkerLocation = (p_location) => {
  if (typeof p_location !== "string") return null;

  let poiLocation;
  if (p_location.startsWith("POINT Z")) {
    const regex = /POINT Z \(([^ ]+) ([^ ]+) ([^ ]+)\)/;
    const match = p_location.match(regex);
    if (match) {
      const x = parseFloat(match[1]);
      const y = parseFloat(match[2]);
      // const z = parseFloat(match[3]); // z unused for map marker
      poiLocation = [x, y];
    }
  } else {
    try {
      const obj = JSON.parse(p_location);
      if (obj?.type === "Point" && Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
        poiLocation = [parseFloat(obj.coordinates[0]), parseFloat(obj.coordinates[1])];
      }
    } catch (error) {
      // Silent: invalid JSON format
    }
  }
  if (!poiLocation) return null;
  const [x, y] = poiLocation;
  if ([x, y].some(v => Number.isNaN(v))) return null;
  return { lat: y, lng: x };
};

function ShuttleSettingPois({
  choosenPoisArray,
  item,
  categoriesObject,
  categoriesArray,
  index,
  onPoiClick,
  pathExist,
  displayMission,
}) {
  if (!item) return null;
  const location = convertPOiLocationStringToMarkerLocation(item.poi_location);
  if (!isValidLatLngObject(location)) return null;
  let categoryName = "";
  let choosenPoiIndex;

  // show pois on diplay mission component , else show pois on set new shuttle mission component
  if (displayMission) {
    choosenPoiIndex = index;
  } else {
    choosenPoiIndex =
      Array.isArray(choosenPoisArray) &&
      choosenPoisArray.findIndex(
        (itemArray) => itemArray?.poi_location === item.poi_location
      );
  }
  const wrapper =
    typeof choosenPoiIndex === "number" && choosenPoiIndex > -1
      ? POI_blue_wrapper_Icon
      : POI_yellow_wrapper_Icon;

  if (
    item.poi_category_id &&
    Array.isArray(categoriesArray) &&
    categoriesArray.length > 0
  ) {
    const category = categoriesArray.find(
      (c) => c.category_id === item.poi_category_id
    );
    if (category) categoryName = category.category_name;
  }

  let htmlContent = "";
  if (typeof choosenPoiIndex === "number" && choosenPoiIndex > -1) {
    htmlContent = `<div style="display:flex;justify-content:center;align-items:center;position:relative;bottom:25px;">
        <div style="position:absolute; transform: scale(0.7); ">${wrapper}</div>
        <div style="position:relative;bottom:6px;height:25px;width:35px;display:flex;justify-content:center;align-items:center;font-size:16px;color:white;"><b>${choosenPoiIndex + 1
      }</b></div>
        </div>`;
  } else {
  // Always render only the wrapper (no category icon)
  htmlContent = `<div style="display:flex;justify-content:center;align-items:center;position:relative;bottom:12px;">
      <div style="position:absolute; transform: scale(0.7);">${wrapper}</div>
    </div>`;
  }

  return (
    <>
      {pathExist && choosenPoiIndex === -1 ? (
        <></>
      ) : (
        <Marker
          key={index}
          eventHandlers={{
            mouseover: (e) => {
              e.target.openPopup();
            },
            mouseout: (e) => {
              e.target.closePopup();
            },
            click: (e) => {
              // If a click handler is provided, invoke it. Otherwise keep legacy guard.
              if (typeof onPoiClick === 'function') {
                onPoiClick(item);
              } else if (!pathExist && choosenPoiIndex > -1 && !displayMission) {
                // Backward compatibility for previous behavior
                onPoiClick && onPoiClick(item);
              } else {
                return null;
              }
            },
          }}
          icon={L.divIcon({
            className: "my-custom-pin",
            html: htmlContent,
          })}
          position={location}
        >
          <Popup position={"top-center"} offset={[0, -20]}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: 100,
                minHeight: 40,
                backgroundColor: 'rgba(0,0,0,0.8)',
                color: "white"
              }}
            >
              <span style={{ color: "white", fontSize: "1rem" }}>
                {item?.poi_name || "Unknown POI"}
              </span>
            </div>
          </Popup>
        </Marker>
      )}
    </>
  );
}

const MemoizedShuttleSettingPoiIcon = React.memo(ShuttleSettingPois);

function PoiICon({ item, categoriesObject, categoriesArray, index }) {
  if (!item) return null;
  const location = convertPOiLocationStringToMarkerLocation(item.poi_location);
  if (!isValidLatLngObject(location)) return null;
  let categoryName = "";
  if (
    item.poi_category_id &&
    Array.isArray(categoriesArray) &&
    categoriesArray.length > 0
  ) {
    const category = categoriesArray.find(
      (c) => c.category_id === item.poi_category_id
    );
    if (category) categoryName = category.category_name;
  }

  let htmlContent = "";

    htmlContent = `<div style="display:flex;justify-content:center;align-items:center;position:relative;bottom:12px;">
        <div style="position:absolute;">${POI_yellow_wrapper_Icon}</div>
    </div>`;

  return (
    <>
      <Marker
        key={index}
        eventHandlers={{
          click: (e) => {
            e.target.openPopup();
          },
        }}
        icon={L.divIcon({
          className: "my-custom-pin",
          html: htmlContent,
        })}
        position={location}
      >
        <Popup position={"top-center"} offset={[0, -20]}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#fff",
              padding: "8px",
              borderRadius: "8px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              border: "1px solid #e0e0e0",
            }}
          >
            <span style={{ color: "grey", opacity: 0.8, fontSize: "0.7rem" }}>
              {categoryName !== "" ? categoryName : null}
            </span>
            <span style={{ color: "black", fontSize: "1rem", fontWeight: "bold" }}>
              {item?.poi_name || "Unknown POI"}
            </span>
          </div>
        </Popup>

      </Marker>
    </>
  );
}

const MemoizedPoiIcon = React.memo(PoiICon);

export const Map = React.memo((props) => {
  const { relevantMapData, isPhoneSize } = useAuth();
  // Use props.mapData as fallback if available
  const mapData = props.mapData || relevantMapData;
  
  const [mapInstance, SetMapInstance] = useState(null);
  const [isCalculatedZoom, setIsCalculatedZoom] = useState(true);
  const [map_coords_buttom_left, setMap_coords_buttom_left] = useState(null);
  const [map_coords_top_right, setMap_coords_top_right] = useState(null);
  const [map_default_point, setMap_default_point] = useState(null);
  const [onHoverCart, setOnHoverCart] = useState(null);
  const [zoom, setZoom] = useState(props.zoom || 20);
  const [staticPopup, setStaticPopup] = React.useState({
    position: null,
    cartInfo: null,
  });
  const [imageUrl, setImageUrl] = useState(null);

  // Add proper image loading states
  const [imageLoading, setImageLoading] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [mappingLoadError, setMappingLoadError] = useState("");
  const [mapLoadState, setMapLoadState] = useState("loading");
  const imageOverlayRef = useRef(null);

  // Use props for static popup if provided (for SiteOverview management)
  const actualStaticPopup = props.disableCartPopup ? { position: null, cartInfo: null } : (props.staticPopup || staticPopup);
  const actualSetStaticPopup = props.disableCartPopup ? null : (props.setStaticPopup || setStaticPopup);

  // Create task icons markup arrays
  const taskIconsMarkup = useMemo(() => {
    if (!Array.isArray(props.tasksArray)) return [];
    return props.tasksArray.map((taskGroup) => {
      const tasks = Array.isArray(taskGroup?.tasks) ? taskGroup.tasks : [];
      return tasks.map((task) => {
        if (!task) return '';
        const iconElements = [];
        if (task.task_type === 'TIME') iconElements.push(renderToStaticMarkup(<ClockIcon />));
        if (task.task_type === 'SMS') iconElements.push(renderToStaticMarkup(<SmsIcon />));
        if (task.task_type === 'SOUND') iconElements.push(renderToStaticMarkup(<SoundIcon />));
        return iconElements.join('');
      }).join('');
    });
  }, [props.tasksArray]);

  const unresolvedTaskIconsMarkup = useMemo(() => {
    if (!Array.isArray(props.unresolvedTasks)) return [];
    return props.unresolvedTasks.map(task => {
      const taskItems = Array.isArray(task?.task_json) ? task.task_json : [];
      return taskItems.map(taskItem => {
        if (!taskItem) return '';
        const iconElements = [];
        if (taskItem.task_type === 'TIME') iconElements.push(renderToStaticMarkup(<ClockIcon />));
        if (taskItem.task_type === 'SMS') iconElements.push(renderToStaticMarkup(<SmsIcon />));
        if (taskItem.task_type === 'SOUND') iconElements.push(renderToStaticMarkup(<SoundIcon />));
        return iconElements.join('');
      }).join('');
    });
  }, [props.unresolvedTasks]);

  // Build a flat list of task marker positions to hide overlapping POIs
  const taskPositions = useMemo(() => {
    const positions = [];
    try {
      // From Step 0 gray task markers
      if (Array.isArray(props.tasksArray)) {
        props.tasksArray.forEach((g) => {
          if (Array.isArray(g.position) && g.position.length >= 2) {
            const lat = toFiniteNumber(g.position[0]);
            const lng = toFiniteNumber(g.position[1]);
            if (lat != null && lng != null) positions.push({ lat, lng });
          }
        });
      }
      // From unresolved tasks
      if (Array.isArray(props.unresolvedTasks)) {
        props.unresolvedTasks.forEach((t) => {
          if (Array.isArray(t.position) && t.position.length >= 2) {
            const lat = toFiniteNumber(t.position[0]);
            const lng = toFiniteNumber(t.position[1]);
            if (lat != null && lng != null) positions.push({ lat, lng });
          }
        });
      }
      // From current paths (points that have task_json)
      if (Array.isArray(props.paths)) {
        props.paths.forEach((p) => {
          const pts = p?.path?.points;
          if (Array.isArray(pts)) {
            pts.forEach((pt) => {
              if (pt && pt.task_json && pt.task_json !== "") {
                const latLng = convertPathPoitToLatLng(pt);
                if (Array.isArray(latLng) && latLng.length >= 2) {
                  const lat = toFiniteNumber(latLng[0]);
                  const lng = toFiniteNumber(latLng[1]);
                  if (lat != null && lng != null) positions.push({ lat, lng });
                }
              }
            });
          }
        });
      }
    } catch {}
    return positions;
  }, [props.tasksArray, props.unresolvedTasks, props.paths]);

  const filteredPoisList = useMemo(() => {
    if (!Array.isArray(props.poisList) || props.poisList.length === 0) return [];
    const thresholdMeters = 10; // hide POI if within ~10m of a task marker
    return props.poisList.filter((poi) => {
      const loc = convertPOiLocationStringToMarkerLocation(poi?.poi_location);
      if (!loc) return true; // keep if invalid loc
      // if any task position is within threshold, hide this POI
      for (const tp of taskPositions) {
        if (haversineMeters(loc, tp) < thresholdMeters) return false;
      }
      return true;
    });
  }, [props.poisList, taskPositions]);

  // Normalize coordinates to handle both 2D and 3D coordinates
  const normalizeCoordinates = (coords) => {
    if (!Array.isArray(coords)) return null;
    // Take only the first two elements (x,y) if we have more and validate them.
    return toLatLngPair(coords.slice(0, 2));
  };

  // Update map coordinates and validate image
  useEffect(() => {
    // console.log('Map data effect triggered:', { 
    //   hasMapData: !!mapData,
    //   hasData: !!mapData?.data,
    //   mapDataKeys: mapData?.data ? Object.keys(mapData.data) : 'no data'
    // });

    if (!mapData) {
      // Still waiting for mapping payload; do not show error yet.
      setMapLoadState("loading");
      setImageLoading(true);
      setMappingLoadError("");
      return;
    }

    if (!mapData?.data) {
      // console.warn('Map data is missing or invalid:', mapData);
      
      // Set default coordinates for basic map functionality
      // Only update array states if they actually changed.
      // (Setting fresh arrays every render causes "Maximum update depth exceeded".)
      const bl = [FALLBACK_DEFAULT_POINT[0] - FALLBACK_MARGIN, FALLBACK_DEFAULT_POINT[1] - FALLBACK_MARGIN];
      const tr = [FALLBACK_DEFAULT_POINT[0] + FALLBACK_MARGIN, FALLBACK_DEFAULT_POINT[1] + FALLBACK_MARGIN];

      setMap_coords_buttom_left((prev) => (sameLatLng(prev, bl) ? prev : bl));
      setMap_coords_top_right((prev) => (sameLatLng(prev, tr) ? prev : tr));
      setMap_default_point((prev) =>
        sameLatLng(prev, FALLBACK_DEFAULT_POINT) ? prev : [...FALLBACK_DEFAULT_POINT]
      );
      setImageLoadError(true);
      setImageLoading(false);
      setImageUrl(null);
      setMappingLoadError("Map mapping cannot be loaded because map data is missing.");
      setMapLoadState("error");
      return;
    }
    let isMounted = true;
    setMapLoadState("loading");
    setMappingLoadError("");

    const updateMapCoords = async () => {
      if (!isMounted) return;

      const data = mapData.data;
      // Normalize coordinates before setting them
      const rawBottomLeft = normalizeCoordinates(data.map_coords_buttom_left);
      const rawTopRight = normalizeCoordinates(data.map_coords_top_right);
      const rawDefaultPoint = normalizeCoordinates(data.map_default_point);
      const normalizedBottomLeft =
        rawBottomLeft || [
          FALLBACK_DEFAULT_POINT[0] - FALLBACK_MARGIN,
          FALLBACK_DEFAULT_POINT[1] - FALLBACK_MARGIN,
        ];
      const normalizedTopRight =
        rawTopRight || [
          FALLBACK_DEFAULT_POINT[0] + FALLBACK_MARGIN,
          FALLBACK_DEFAULT_POINT[1] + FALLBACK_MARGIN,
        ];
      const normalizedDefaultPoint =
        rawDefaultPoint || [...FALLBACK_DEFAULT_POINT];
      const hasInvalidCoordinateMapping =
        !rawBottomLeft || !rawTopRight || !rawDefaultPoint;

      console.log('Updating map coordinates:', {
        currentDefaultPoint: map_default_point,
        newDefaultPoint: normalizedDefaultPoint,
        hasMapImage: !!mapData.picture,
        imageSize: mapData.picture?.length || 0,
        coordinates: {
          bottomLeft: normalizedBottomLeft,
          topRight: normalizedTopRight,
          originalBottomLeft: data.map_coords_buttom_left,
          originalTopRight: data.map_coords_top_right
        }
      });

      if (JSON.stringify(normalizedDefaultPoint) !== JSON.stringify(map_default_point)) {
        if (mapData.picture) {
          console.log('Starting image load process...');
          setImageLoading(true);

          const img = new Image();
          img.onload = () => {
            if (!isMounted) {
              console.log('Image loaded but component unmounted, skipping state updates');
              return;
            }
            console.log('Map image loaded successfully');
            setMap_coords_buttom_left(normalizedBottomLeft);
            setMap_coords_top_right(normalizedTopRight);
            setMap_default_point(normalizedDefaultPoint);
            setImageLoadError(false);
            setImageLoading(false);
            setMapLoadState(hasInvalidCoordinateMapping ? "error" : "ready");
            setMappingLoadError(
              hasInvalidCoordinateMapping
                ? "Map mapping is partially invalid. Some map coordinates were missing or malformed, fallback coordinates were used."
                : ""
            );
          };

          img.onerror = (error) => {
            console.error('Failed to load map image:', {
              error,
              imageSize: mapData.picture?.length,
              isBase64Valid: /^[A-Za-z0-9+/=]+$/.test(mapData.picture || ''),
              coordinatesPresent: {
                bottomLeft: !!data.map_coords_buttom_left,
                topRight: !!data.map_coords_top_right,
                defaultPoint: !!data.map_default_point
              },
              imageState: {
                loading: imageLoading,
                error: imageLoadError
              }
            });
            // Fallback to base map even on error
            setMap_coords_buttom_left(normalizedBottomLeft);
            setMap_coords_top_right(normalizedTopRight);
            setMap_default_point(normalizedDefaultPoint);
            setImageLoadError(true);
            setImageLoading(false);
            setImageUrl(null);
            setMappingLoadError("Map image cannot be loaded. Falling back to base map data.");
            setMapLoadState("error");
          };

          try {
            console.log('Setting image source, base64 length:', mapData.picture?.length);
            const url = `data:image/png;base64,${mapData.picture}`;
            img.src = url;
            setImageUrl(url);  // Set the image URL state
          } catch (error) {
            console.error('Error setting image source:', {
              error,
              imageSize: mapData.picture?.length,
              base64Start: mapData.picture?.substring(0, 100) + '...'
            });
            // Fallback when setting image source fails
            setMap_coords_buttom_left(normalizedBottomLeft);
            setMap_coords_top_right(normalizedTopRight);
            setMap_default_point(normalizedDefaultPoint);
            setImageLoadError(true);
            setImageLoading(false);
            setImageUrl(null);  // Clear the image URL on error
            setMappingLoadError("Map image payload is invalid and could not be decoded.");
            setMapLoadState("error");
          }
        } else {
          console.warn('No map image data available');
          // Fallback: still set coordinates & default point so base OSM tiles render
            setMap_coords_buttom_left(normalizedBottomLeft);
            setMap_coords_top_right(normalizedTopRight);
            setMap_default_point(normalizedDefaultPoint);
          setImageLoadError(true);
          setImageLoading(false);
          setImageUrl(null);
          setMappingLoadError("Map image is missing. The custom map layer cannot be displayed.");
          setMapLoadState("error");
        }
      } else {
        // Map default point already applied; still resolve current load state.
        if (!mapData.picture) {
          setImageLoadError(true);
          setImageLoading(false);
          setImageUrl(null);
          setMappingLoadError("Map image is missing. The custom map layer cannot be displayed.");
          setMapLoadState("error");
        } else {
          setImageLoading(false);
          setImageLoadError(false);
          setMapLoadState(hasInvalidCoordinateMapping ? "error" : "ready");
          setMappingLoadError(
            hasInvalidCoordinateMapping
              ? "Map mapping is partially invalid. Some map coordinates were missing or malformed, fallback coordinates were used."
              : ""
          );
        }
      }
    };

    updateMapCoords();
    return () => {
      isMounted = false;
    };
  }, [mapData, map_default_point]);

  // Validate coordinates and default point
  const validCoordinates = useMemo(() => {
    if (!map_coords_buttom_left || !map_coords_top_right || !map_default_point) return false;

    const isValidArray = (arr) => Array.isArray(arr) && arr.length === 2 && 
      !isNaN(Number(arr[0])) && !isNaN(Number(arr[1])) &&
      Number(arr[0]) !== 0 && Number(arr[1]) !== 0; // Exclude invalid zero coordinates

    return isValidArray(map_coords_buttom_left) &&
           isValidArray(map_coords_top_right) &&
           isValidArray(map_default_point);
  }, [map_coords_buttom_left, map_coords_top_right, map_default_point]);

  // Keep the overlay pinned to the exact server-provided coordinates.
  const overlayBounds = useMemo(() => {
    if (!validCoordinates) return null;

    return L.latLngBounds(
      [
        Number(map_coords_buttom_left[0]),
        Number(map_coords_buttom_left[1])
      ],
      [
        Number(map_coords_top_right[0]),
        Number(map_coords_top_right[1])
      ]
    );
  }, [map_coords_buttom_left, map_coords_top_right, validCoordinates]);

  // Apply any visual breathing room only to map framing, not overlay placement.
  const viewBounds = useMemo(() => {
    if (!validCoordinates) return null;

    const margin = !isPhoneSize ? 0.0001 : 0.0002;
    return L.latLngBounds(
      [
        Number(map_coords_buttom_left[0]) - margin,
        Number(map_coords_buttom_left[1]) - margin
      ],
      [
        Number(map_coords_top_right[0]) + margin,
        Number(map_coords_top_right[1]) + margin
      ]
    );
  }, [map_coords_buttom_left, map_coords_top_right, isPhoneSize, validCoordinates]);

  // Reverse point picking (UI only): strict road-vertex selection
  const REVERSE_VERTEX_THRESHOLD_METERS = 12;
  const findNearestRoadVertex = (clickedLatLng) => {
    const roadsList = props.roadsList;
    if (!roadsList || typeof roadsList !== "object") return null;
    if (!clickedLatLng || clickedLatLng.lat == null || clickedLatLng.lng == null) return null;

    let best = null;
    let bestDist = Infinity;
    try {
      for (const road of Object.values(roadsList)) {
        if (!Array.isArray(road)) continue;
        for (const pt of road) {
          if (!Array.isArray(pt) || pt.length < 2) continue;
          // road vertices come as [lng, lat] (see formatedTrackPath)
          const lng = Number(pt[0]);
          const lat = Number(pt[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
          const d = haversineMeters(clickedLatLng, { lat, lng });
          if (d < bestDist) {
            bestDist = d;
            best = { lat, lng };
          }
        }
      }
    } catch {
      return null;
    }

    if (!best || bestDist > REVERSE_VERTEX_THRESHOLD_METERS) return null;
    return best;
  };

  // Cursor hint while picking reverse point
  useEffect(() => {
    if (!mapInstance) return;
    try {
      const container = mapInstance.getContainer?.();
      if (!container) return;
      const prev = container.style.cursor;
      container.style.cursor = props.reversePointPick ? "crosshair" : "";
      return () => {
        container.style.cursor = prev;
      };
    } catch {}
  }, [mapInstance, props.reversePointPick]);


  function MyComponent() {
    const map = useMapEvents({
      click: (e) => {
        if (props.reversePointPick && typeof props.setReversePoint === "function") {
          const clicked = e?.latlng ? { lat: e.latlng.lat, lng: e.latlng.lng } : null;
          const nearest = findNearestRoadVertex(clicked);
          if (nearest) {
            props.setReversePoint(nearest);
          }
        }
        if (onHoverCart) {
          setOnHoverCart(null);
        }
        if (staticPopup) {
          setStaticPopup({ position: null, cartInfo: null });
        }
      },
      dragstart: (e) => {

        setIsCalculatedZoom(false);
      },
      zoom: () => {
        setIsCalculatedZoom(false);
      },
      zoomend: (e) => {
        if (props.setZoom) {
          props.setZoom(map.getZoom());
        }
      },
    });
    return null;
  }

useEffect(() => {
  if (!props.mapRef || !props.selectedCart || !props.carsLocations) return;

  let intervalId = setInterval(() => {
    try {
      const cartInfo = props.carsLocations.find(
        (cart) => cart.cart_number === props.selectedCart
      );
      if (!cartInfo?.cart_location) return;

      // Parse cart location
      let cartLat, cartLng;
      if (Array.isArray(cartInfo.cart_location)) {
        [cartLat, cartLng] = cartInfo.cart_location;
      } else if (cartInfo.cart_location.lat !== undefined) {
        cartLat = cartInfo.cart_location.lat;
        cartLng = cartInfo.cart_location.lng;
      } else {
        return;
      }
      const map = props.mapRef.current;
      const cartLocation = L.latLng(cartLat, cartLng);
      map.setView(cartLocation, map.getZoom(), {
          animate: true,
          duration: 1.2,
        });
      
    } catch (error) {
      // Silently ignore errors
    }
  }, 200);

  return () => clearInterval(intervalId);
}, [ props.selectedCart, props.carsLocations]);


  //Set focus on cart from cart list view
  useEffect(() => {
    if (!mapInstance) return;

    // Add a delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      try {
        // More robust DOM check
        const container = mapInstance.getContainer();
        if (!container || !document.body.contains(container)) {
          console.warn('Map container not in DOM, skipping focus operation');
          return;
        }

        // Ensure map is fully loaded before operations
        if (!mapInstance._loaded) {
          console.warn('Map not fully loaded, skipping focus operation');
          return;
        }

        let locationToFocus = null;
        const map = mapInstance;

        if (props.focusOnCart && props.focusOnCart != null) {
          if (props.focusOnCart?.type === "cart") {
            if (props.carsLocations) {
              for (const cartInfo of Object.values(props.carsLocations)) {
                if (cartInfo.cart_number === props.focusOnCart.id) {
                  locationToFocus = cartInfo.cart_location;
                }
              }
            }
          } else {
            //type is poi
            if (props.poisList && Array.isArray(props.poisList)) {
              for (const poi of props.poisList) {
                if (poi.poi_id === props.focusOnCart.id) {
                  locationToFocus = convertPOiLocationStringToMarkerLocation(
                    poi.poi_location
                  );
                }
              }
            }
          }
        }

        let zoomToFocus = map.getZoom();

        if (locationToFocus == null) {
          locationToFocus = map_default_point;
        }

        // Validate location before setting view
        if (locationToFocus && 
            ((Array.isArray(locationToFocus) && locationToFocus.length >= 2) || 
             (locationToFocus.lat !== undefined && locationToFocus.lng !== undefined))) {
          map.setView(locationToFocus, zoomToFocus, { animate: true, duration: 1.2 });
        } else {
          console.warn('Invalid locationToFocus:', locationToFocus);
        }
      } catch (error) {
        console.error('Error in focus on cart effect:', error);
      }
    }, 500); // Add delay to ensure DOM readiness

    return () => clearTimeout(timeoutId);
  }, [mapInstance, props.focusOnCart, map_default_point]);

  const getPoiIconWithIndex = (iconMarkup, index) => {
    return `
          <div style="position: relative; display: flex; justify-content: center; align-items: center;">
            <div style="position: absolute;">${iconMarkup}</div>
            <div
              style="position: relative; bottom: 6px; height: 25px; width: 25px; border-radius: 50%;
              display: flex; justify-content: center; align-items: center; font-size: 16px; color: black; font-weight: bold;">
              ${index + 1}
            </div>
          </div>
        `;
  };
  const nonStopAreaValidation = useMemo(() => {
    if (!Array.isArray(props.nonStopArea)) return { polygons: [], invalidCount: 0 };
    let invalidCount = 0;
    const polygons = props.nonStopArea
      .map((area) => convertWKTToLatLngArray(area?.geometry))
      .filter((positions) => {
        if (!isValidPolygonPositions(positions)) {
          invalidCount += 1;
          return false;
        }
        return true;
      });
    return { polygons, invalidCount };
  }, [props.nonStopArea]);

  const boundariesValidation = useMemo(() => {
    if (!props.bounderiesList || typeof props.bounderiesList !== "object") {
      return { polygons: [], invalidCount: 0 };
    }
    let invalidCount = 0;
    const polygons = Object.entries(props.bounderiesList).map(([, value]) => {
      if (!Array.isArray(value)) {
        invalidCount += 1;
        return [];
      }
      return value
        .map((item) => formatedTrackPath(item))
        .filter((positions) => {
          if (!isValidPolygonPositions(positions)) {
            invalidCount += 1;
            return false;
          }
          return true;
        });
    });
    return { polygons, invalidCount };
  }, [props.bounderiesList]);

  const safeRoadPathEntries = useMemo(() => {
    if (!props.roadsPathList || typeof props.roadsPathList !== "object") {
      return { entries: [], invalidCount: 0 };
    }
    let invalidCount = 0;
    const entries = Object.entries(props.roadsPathList).filter(([, value]) => {
      const positions = formatedTrackPath(value);
      const isValid = isValidPolylinePositions(positions);
      if (!isValid) invalidCount += 1;
      return isValid;
    });
    return { entries, invalidCount };
  }, [props.roadsPathList]);

  const safeRoadEntries = useMemo(() => {
    if (!props.roadsList || typeof props.roadsList !== "object") {
      return { entries: [], invalidCount: 0 };
    }
    let invalidCount = 0;
    const entries = Object.entries(props.roadsList)
      .map(([key, value]) => [key, formatedTrackPath(value)])
      .filter(([, positions]) => {
        const isValid = isValidPolylinePositions(positions);
        if (!isValid) invalidCount += 1;
        return isValid;
      });
    return { entries, invalidCount };
  }, [props.roadsList]);

  const safeShuttlePaths = useMemo(() => {
    if (!Array.isArray(props.paths)) return { entries: [], invalidCount: 0 };
    let invalidCount = 0;
    const entries = props.paths.filter((pathItem) => {
      const isValid =
        pathItem?.path &&
        pathItem.path.path_id != null &&
        Array.isArray(pathItem.path.points);
      if (!isValid) invalidCount += 1;
      return isValid;
    });
    return { entries, invalidCount };
  }, [props.paths]);

  const safeSkeletonPoints = useMemo(() => {
    if (!Array.isArray(props.skeletonPoints)) return { entries: [], invalidCount: 0 };
    let invalidCount = 0;
    const entries = props.skeletonPoints.filter((point) => {
      const position = toLatLngPair([point?.lat, point?.lng]);
      if (!position) {
        invalidCount += 1;
        return false;
      }
      return true;
    });
    return { entries, invalidCount };
  }, [props.skeletonPoints]);

  const safeCreatePathMarkers = useMemo(() => {
    if (!Array.isArray(props.createPathMarkers)) return { entries: [], invalidCount: 0 };
    let invalidCount = 0;
    const entries = props.createPathMarkers
      .map((location, index) => {
        const position = toLatLngPair(location);
        if (!position) {
          invalidCount += 1;
          return null;
        }
        return { position, originalIndex: index };
      })
      .filter(Boolean);
    return { entries, invalidCount };
  }, [props.createPathMarkers]);

  const safeMemoEntries = useMemo(() => {
    if (!Array.isArray(props.memoList)) return { entries: [], invalidCount: 0 };
    let invalidCount = 0;
    const entries = props.memoList
      .map((item, index) => {
        const position = parseMemoPosition(item?.memo_location);
        if (!position) {
          invalidCount += 1;
          return null;
        }
        return { item, index, position };
      })
      .filter(Boolean);
    return { entries, invalidCount };
  }, [props.memoList]);

  const invalidTaskMarkersCount = useMemo(() => {
    if (!Array.isArray(props.tasksArray)) return 0;
    return props.tasksArray.reduce(
      (count, taskGroup) => count + (toLatLngPair(taskGroup?.position) ? 0 : 1),
      0
    );
  }, [props.tasksArray]);

  const invalidUnresolvedTaskMarkersCount = useMemo(() => {
    if (!Array.isArray(props.unresolvedTasks)) return 0;
    return props.unresolvedTasks.reduce(
      (count, task) => count + (toLatLngPair(task?.position) ? 0 : 1),
      0
    );
  }, [props.unresolvedTasks]);

  const roadHistoryStartPosition = toLatLngPair([
    props.roadRecordingHistory?.pathPoints?.[0]?.position?.y,
    props.roadRecordingHistory?.pathPoints?.[0]?.position?.x,
  ]);
  const roadHistoryEndPosition = toLatLngPair([
    props.roadRecordingHistory?.pathPoints?.at?.(-1)?.position?.y,
    props.roadRecordingHistory?.pathPoints?.at?.(-1)?.position?.x,
  ]);

  const mappingValidationWarnings = useMemo(() => {
    const warnings = [];
    if (boundariesValidation.invalidCount > 0) {
      warnings.push(
        `${boundariesValidation.invalidCount} boundary item(s) were skipped because mapping data is malformed.`
      );
    }
    if (nonStopAreaValidation.invalidCount > 0) {
      warnings.push(
        `${nonStopAreaValidation.invalidCount} non-stop area polygon(s) were skipped because geometry is invalid.`
      );
    }
    if (safeRoadPathEntries.invalidCount > 0 || safeRoadEntries.invalidCount > 0) {
      warnings.push("Some roads were skipped because coordinates are missing or invalid.");
    }
    if (invalidTaskMarkersCount > 0 || invalidUnresolvedTaskMarkersCount > 0) {
      warnings.push("Some task markers were skipped because position data is invalid.");
    }
    if (safeShuttlePaths.invalidCount > 0 || safeSkeletonPoints.invalidCount > 0) {
      warnings.push("Some shuttle path items were skipped because required fields are missing.");
    }
    if (safeCreatePathMarkers.invalidCount > 0 || safeMemoEntries.invalidCount > 0) {
      warnings.push("Some map markers were skipped because mapping payload is malformed.");
    }
    return warnings;
  }, [
    boundariesValidation.invalidCount,
    nonStopAreaValidation.invalidCount,
    safeRoadPathEntries.invalidCount,
    safeRoadEntries.invalidCount,
    invalidTaskMarkersCount,
    invalidUnresolvedTaskMarkersCount,
    safeShuttlePaths.invalidCount,
    safeSkeletonPoints.invalidCount,
    safeCreatePathMarkers.invalidCount,
    safeMemoEntries.invalidCount,
  ]);


  // Helper function to validate and adjust bounds
  const validateBounds = (bounds) => {
    if (!bounds || !bounds.isValid()) {
      console.warn('Invalid bounds provided:', bounds);
      return null;
    }

    // Ensure bounds are within valid range
    const adjustedBounds = L.latLngBounds([
      Math.max(-90, Math.min(90, bounds.getSouth())),
      Math.max(-180, Math.min(180, bounds.getWest()))
    ], [
      Math.max(-90, Math.min(90, bounds.getNorth())),
      Math.max(-180, Math.min(180, bounds.getEast()))
    ]);

    console.log('Adjusted bounds:', adjustedBounds.toBBoxString());
    return adjustedBounds;
  };

  React.useEffect(() => {
    if (mapInstance && viewBounds) {
      // Add a delay to ensure DOM is ready and avoid _leaflet_pos errors
      const timeoutId = setTimeout(() => {
        try {
          const validBounds = validateBounds(viewBounds);
          if (validBounds && mapInstance.getContainer()) {
            // More robust DOM check
            const container = mapInstance.getContainer();
            if (!container || !document.body.contains(container)) {
              console.warn('Map container not in DOM, skipping bounds operation');
              return;
            }

            // Ensure the map is properly initialized
            if (!mapInstance._loaded) {
              console.warn('Map not fully loaded yet, deferring bounds operation');
              // Retry after map is loaded
              mapInstance.whenReady(() => {
                setTimeout(() => {
                  try {
                    const container = mapInstance.getContainer();
                    if (container && document.body.contains(container)) {
                      const paddingOptions = {
                        padding: [50, 50],
                        maxZoom: 20,
                        animate: false
                      };
                      console.log('Fitting to bounds (deferred):', validBounds.toBBoxString());
                      mapInstance.fitBounds(validBounds, paddingOptions);
                    }
                  } catch (error) {
                    console.error('Error in deferred bounds fitting:', error);
                  }
                }, 200);
              });
              return;
            }

            // Add padding to ensure visibility
            const paddingOptions = {
              padding: [50, 50],
              maxZoom: 20,
              animate: false // Disable animation to prevent timing issues
            };

            console.log('Fitting to bounds:', validBounds.toBBoxString());
            mapInstance.fitBounds(validBounds, paddingOptions);

            // Update map view if needed
            if (!mapInstance.getBounds().contains(validBounds)) {
              console.log('Adjusting view to contain bounds');
              mapInstance.setView(validBounds.getCenter(), mapInstance.getBoundsZoom(validBounds), { animate: false });
            }
          }
        } catch (error) {
          console.error('Error in bounds fitting:', error);
        }
      }, 300); // Increased delay to ensure DOM is ready

      return () => clearTimeout(timeoutId);
    }
  }, [mapInstance, viewBounds]);



  return (
    <React.Fragment>
      {validCoordinates && map_default_point && mapData?.picture ? (        <>
          <MapContainer
            ref={(mapRef) => {
              try {
                if (mapRef && !mapInstance) {
                  console.log('MapContainer ref callback called!', mapRef);
                  const leafletMap = mapRef.getMap ? mapRef.getMap() : mapRef;
                  if (leafletMap && typeof leafletMap.setView === 'function') {
                    console.log('Setting mapInstance from ref callback');
                    SetMapInstance(leafletMap);
                    if (props.mapRef) {
                      props.mapRef.current = leafletMap;
                    }
                  }
                }
                if (props.mapRef && mapRef) {
                  const leafletMap = mapRef.getMap ? mapRef.getMap() : mapRef;
                  props.mapRef.current = leafletMap;
                }
              } catch (error) {
                console.error('Error in ref callback:', error);
              }
            }}
          key={`map-${JSON.stringify(map_default_point)}`}
          center={Array.isArray(map_default_point) ? map_default_point : [32.0853, 34.7818]}
          maxZoom={25}
          minZoom={15}
          zoomControl={false} // disabled to avoid duplicate default zoom buttons (custom controls used)
          scrollWheelZoom={true}
          zoom={props.zoom ? props.zoom : 18}
          touchZoom={true}
          attributionControl={true}
          zoomDelta={1}
          wheelPxPerZoomLevel={120}
          zoomAnimation={true}
          maxBounds={viewBounds}
          maxBoundsViscosity={1.0}
          style={{
            backgroundColor: "#F0F0F3",
            height: props.mapHeight || "100%",
            width: "100%",
          }}
          closePopupOnClick
          whenCreated={(mapInstance) => {
            console.log('MapContainer whenCreated called!', {
              mapInstance,
              isValidInstance: !!mapInstance,
              hasSetView: typeof mapInstance?.setView === 'function',
              bounds: viewBounds,
              boundsValid: viewBounds?.isValid(),
              hasContainer: !!mapInstance?.getContainer?.()
            });
            
            try {
              // Ensure map is properly initialized before setting state
              if (mapInstance && typeof mapInstance.setView === 'function') {
                console.log('Setting mapInstance from whenCreated');
                
                // Use a small delay to ensure the container is fully in DOM
                setTimeout(() => {
                  SetMapInstance(mapInstance);
                  
                  if (props.mapRef) {
                    props.mapRef.current = mapInstance;
                    console.log('props.mapRef.current set');
                  }
                }, 100);
                
                // Wait for the map to be fully ready before any operations
                mapInstance.whenReady(() => {
                  console.log('Map is fully ready for operations');
                  // The bounds will be set by the useEffect, no need to do it here
                });
                
                console.log('Map instance created successfully');
              } else {
                console.error('Invalid map instance in whenCreated:', mapInstance);
              }
              
            } catch (error) {
              console.error('Error in whenCreated callback:', error);
            }
          }}
        >
          <MyComponent />

          {/* Base tile layer */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            zIndex={0}
            maxNativeZoom={19}
            maxZoom={25}
          />

          {/* Image overlay with bounds validation */}
          {imageUrl && overlayBounds && overlayBounds.isValid() && (
            <ImageOverlay
              key={`overlay-${imageUrl}-${overlayBounds.toBBoxString()}`}
              url={imageUrl}
              bounds={overlayBounds}
              opacity={1}
              zIndex={10}
            />
          )}

          {mapLoadState !== "loading" &&
            (mappingLoadError || mappingValidationWarnings.length > 0) && (
            <div
              style={{
                position: "absolute",
                top: 12,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1300,
                width: "min(680px, calc(100% - 24px))",
              }}
            >
              <Alert severity={mappingLoadError ? "error" : "warning"} variant="filled">
                {mappingLoadError || mappingValidationWarnings[0]}
              </Alert>
            </div>
          )}

          {
            nonStopAreaValidation.polygons?.map((positions, index) => (
              <Polygon
                key={index}
                positions={positions}
                fillOpacity={0.3}
                stroke={true}
                color="#000000"
                weight={2}
                dashArray="4 4"
              />
            ))
          }

          {boundariesValidation.polygons.length > 0 &&
            props.showBoundaries &&
            boundariesValidation.polygons.map((boundaryGroup, groupIndex) =>
              boundaryGroup.map((positions, index) => (
                <Polygon
                  key={`${groupIndex}-${index}`}
                  weight={3}
                  pathOptions={{
                    fillColor: "black",
                    color: "#FFD02C",
                    opacity: 0.8,
                    interactive: true,
                  }}
                  positions={positions}
                />
              ))
            )}


          {props.roadsPathList != undefined &&
            props.showPathRoads &&
            safeRoadPathEntries.entries.map(([k, v]) => (
              <Road
                key={k}
                roadData={v}
                roadIndex={k}
                onRoadClick={props.onRoadClick} // Pass the onRoadClick function
              />
            ))}

          {props.roadsList != undefined && props.showRoads
            ? safeRoadEntries.entries.map(([k, positions]) => (
              <Polyline
                key={k}
                weight={"4"}
                pathOptions={{
                  color: "#009DDC",
                  lineCap: "round",
                  lineJoin: "miter",
                  zIndex: 10,
                }}
                positions={positions}
              ></Polyline>
            ))
            : null}

          {props.taskActive &&
            Array.isArray(props.tasksArray) &&
            props.tasksArray.length > 0 &&
            props.tasksArray.map((taskGroup, index) => {
              const position = toLatLngPair(taskGroup?.position);
              if (!position) return null;
              return (
                <Marker
                  key={`task-group-${index}`}
                  position={position}
                  icon={L.divIcon({
                    className: "my-custom-task-icon",
                    html: generateIconHtmlContent(
                      "T",
                      taskIconsMarkup[index],
                      "gray"
                    ),
                  })}
                />
              );
            })}

          {props.unresolvedTasks && props.unresolvedTasks.length > 0 && props.unresolvedTasks.map((task, index) => {
            const position = toLatLngPair(task?.position);
            if (!position) return null;
            return (
              <Marker
                key={`unresolved-task-${index}`}
                position={position}
                icon={L.divIcon({
                  className: "my-custom-task-icon",
                  html: generateIconHtmlContent(
                    "T",
                    unresolvedTaskIconsMarkup[index],
                    props.fixMode !== null ? "red" : "gray"
                  ) // Custom icon for unresolved task
                })}
              >
                <Popup>
                  <div style={{ borderRadius: '10px', display: 'flex', justifyContent: 'space-around', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.8)', color: "white" }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onDeleteUnresolvedTask(index); // Call delete handler
                      }}
                    >
                      <Delete style={{ width: '30px', height: '30px', fill: '#FFFFFF' }} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        props.handleFixUnresolvedTask(index); // Call move handler
                      }}
                    >
                      <Move style={{ width: '30px', height: '30px', fill: '#FFFFFF' }} />
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {props.isShuttleComponent !== undefined &&
            props.isShuttleComponent === true && (
              <>
                <MarkerClusterGroup
                  showCoverageOnHover={false}
                  spiderLegPolylineOptions={{ weight: 0 }}
                  helpingCircles={true}
                  maxClusterRadius={10}
                >
                  {props.paths !== undefined &&
                    Array.isArray(props.paths) &&
                    props.paths.length > 0 &&
                    safeShuttlePaths.entries.map((path, index) => {
                      const hasCustomMain = typeof props.shuttleMainPathIndex === 'number';
                      const startIndex = hasCustomMain ? props.shuttleMainPathIndex : 0;
                      const endIndex = hasCustomMain ? props.shuttleMainPathIndex : (safeShuttlePaths.entries.length -1);
                      const showEndMarker = props.showEndMarker !== undefined ? props.showEndMarker : true; // Default to true for backward compatibility
                      return (
                        <Path
                          key={`shuttlePaths-${path.path.path_id}-${index}`}
                          taskHandlers={props.taskHandlers}
                          onPathClick={
                            props.onPathPointClick !== undefined
                              ? props.onPathPointClick
                              : null
                          }
                          pathIndex={index}
                          path={path}
                          weight={props.pathWeight ? props.pathWeight : "6"}
                          moveMode={props.moveMode}
                          isStart={props.showStartEndMarkers && index === startIndex}
                          isEnd={props.showStartEndMarkers && showEndMarker && index === endIndex}
                          taskActive={props.taskActive}
                          isPreview={props.isPreview}
                        />
                      );
                    })}
                  {/* Skeleton points from backend */}
                  {safeSkeletonPoints.entries.length > 0 &&
                    safeSkeletonPoints.entries.map((sp, idx) => (
                      <Marker
                        key={`skeleton-${idx}-${sp.lat}-${sp.lng}`}
                        position={[Number(sp.lat), Number(sp.lng)]}
                        icon={L.divIcon({
                          className: "my-custom-skeleton-icon",
                          html: generateIconHtmlContent("S", "", "blue"),
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -20]} opacity={1} permanent={false}>
                          <span>Skeleton {typeof sp.seq === 'number' ? `#${sp.seq + 1}` : ''}</span>
                        </Tooltip>
                      </Marker>
                    ))}
                  {/* {
                                            props.tasksArray !== undefined && Array.isArray(props.tasksArray) &&  props.tasksArray.length >0
                                        && 
                                            <>
                                                {props.tasksArray.map((item,index)=>(
                                                    <MemomizedTaskIcon  
                                                        key={index} 
                                                        task={item}
                                                        addAnotherTaskToExistTask={props.addAnotherTaskToExistTask}
                                                    />
                                                ))}
                                            </>   
                                    } */}
                  {filteredPoisList !== undefined &&
                    Array.isArray(filteredPoisList) &&
                    filteredPoisList.length > 0 &&
                    filteredPoisList.map((item, index) => (
                      <MemoizedShuttleSettingPoiIcon
                        onPoiClick={props.onPoiClick}
                        categoriesArray={props.categoriesArray}
                        index={index}
                        item={item}
                        key={index + "" + item.poi_id}
                        categoriesObject={props.categoriesObject}
                        choosenPoisArray={props.choosenPoisArray}
                        displayMission={
                          props.displayMission !== undefined
                            ? props.displayMission
                            : false
                        }
                        pathExist={
                          props.paths !== undefined &&
                          Array.isArray(props.paths)
                        }
                      />
                    ))}
                </MarkerClusterGroup>
              </>
            )}

          {props.addPoi !== undefined && props.addPoi === true && (
            <AddPoiMarker default={props.addPoiDefault} onMapClick={props.onMapClickToAddPoiMarker} />
          )}

          {props.createPathMarkers !== undefined && (
            <MarkerClusterGroup
              showCoverageOnHover={false}
              spiderLegPolylineOptions={{ weight: 0 }}
              helpingCircles={true}
              maxClusterRadius={10}
            >
              {safeCreatePathMarkers.entries.map(({ position, originalIndex }) => {
                const iconMarkup =
                  props.moveMode === originalIndex || props.insertMode === originalIndex
                    ? POI_red_wrapper_Icon // Use a gray icon for markers in move mode
                    : POI_white_wrapper_Icon;
                const iconWithIndex = getPoiIconWithIndex(iconMarkup, originalIndex);

                return (
                  <Marker
                    key={`pathsCreatorMarkers${originalIndex}`}
                    position={position}
                    icon={L.divIcon({
                      className: "my-custom-pin",
                      html: iconWithIndex,
                      iconAnchor: [2, 30], // Adjust these values to move the pin upwards
                    })}
                  >
                    <Popup offset={[0, -20]}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "center",
                          margin: "5px",
                          padding: '10px', // Padding for better spacing
                          borderRadius: '10px',// Rounded corners for a cleaner look
                          backgroundColor: 'rgba(0,0,0,0.8)',
                        }}
                      >
                        <button
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            margin: "5px",
                            borderRadius: "50%",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onDeleteMarker(originalIndex);
                          }}
                        >
                          <Delete style={{ width: '30px', height: '30px', fill: '#FFFFFF' }} />
                        </button>
                        <button
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            margin: "5px",
                            borderRadius: "50%",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onMoveMarker(originalIndex);
                          }}
                        >
                          <Move style={{ width: '30px', height: '30px', fill: '#FFFFFF' }} />
                        </button>
                        <button
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            margin: "5px",
                            borderRadius: "50%",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onInsertMarker(originalIndex);
                          }}
                        >
                          <Insert style={{ width: '30px', height: '30px', fill: '#FFFFFF' }} />
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {props.poisList != undefined &&
            props.showPOI &&
            props.poisList.map((item, index) => (
              <MemoizedPoiIcon
                categoriesArray={props.categoriesArray}
                key={index}
                index={index}
                item={item}
                categoriesObject={props.categoriesObject}
              />
            ))}

          {props.memoList != undefined &&
            props.showMemo &&
            safeMemoEntries.entries.map(({ item, index, position }) => (
              <Marker
                eventHandlers={{
                  click: (e) => {
                    e.target.openPopup();
                  },
                }}
                key={index}
                position={position}
              >
                <Tooltip
                  direction="bottom"
                  offset={[0, 20]}
                  opacity={1}
                  permanent
                >
                  <b style={{ color: "red" }}>
                    Memo:
                    <br />{" "}
                  </b>
                  {item.description}
                </Tooltip>
              </Marker>
            ))}

          {/* Road Live Recordings Display */}
          {props.roadLiveRecordings &&
            props.showRoadRecordings &&
            Array.isArray(props.roadLiveRecordings) &&
            props.roadLiveRecordings.map((recording, index) => (
              <RoadRecordPath
                key={`road-recording-${recording.recordId || index}`}
                recording={recording}
                index={index}
              />
            ))}

          {/* Road History Recording Display */}
          {props.showRoadRecordingHistory && props.roadRecordingHistory && (
            <>
              <RoadRecordPath key={props.roadRecordingHistory.id} recording={props.roadRecordingHistory} />

              {/* Start marker */}
              {roadHistoryStartPosition && (
                <Marker
                  position={roadHistoryStartPosition}
                  icon={L.divIcon({
                    className: "my-custom-pin",
                    html: generateIconHtmlContent("S", "", 'yellow')
                  })}
                />
              )}

              {/* End marker */}
              {roadHistoryEndPosition && (
                <Marker
                  position={roadHistoryEndPosition}
                  icon={L.divIcon({
                    className: "my-custom-pin",
                    html: generateIconHtmlContent("E", "", 'yellow')
                  })}
                />
              )}
            </>

          )}




          {/* use map click handler  */}
          {props.onMapClick !== undefined && (
            <UseMapClickEvent onMapClick={props.onMapClick} />
          )}

          {/* Conditionally render the ChipStack based on props.isPreview - inside MapContainer */}
          {props.isPreview && (
            <div
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                zIndex: 1000, // Ensure it renders above the map
              }}
            >
              <ChipStack useRelativePositioning={true} />
            </div>
          )}

          {props.allExistsCarts != undefined && props.showCarts != undefined ? (
            <MapRef
              cartToShowPath={props.cartToShowPath}
              width={"100%"}
              height={"100%"}
              OnlieMarkers={props.carsLocations}
              OfflineMarkes={props.allExistsCarts}
              setCartToShowPath={props.setCartToShowPath}
              cartsPaths={props.cartsPaths}
              setOnHoverCart={setOnHoverCart}
              onHoverCart={onHoverCart}
              defaultPoint={map_default_point}
              selectedCart={props.selectedCart}
              setSelectedCart={props.setSelectedCart}
              staticPopup={actualStaticPopup}
              setStaticPopup={actualSetStaticPopup}
              isPhoneSize={isPhoneSize}
              onStartReverse={props.onStartReverse}
            />
          ) : null}
          {Array.isArray(props.cartToShowPath) &&
            props.cartToShowPath.length > 0 &&
            props.cartsPaths
            ? props.cartToShowPath.map(
              (cartNumber) =>
                props.cartsPaths[cartNumber]?.path &&
                props.cartsLocationObject?.[cartNumber] &&
                props.cartsPaths[cartNumber].path.map((path, index) => {
                  if (!path?.path || !Array.isArray(path.path.points)) return null;
                  return (
                  <React.Fragment key={path.path.path_id || `${cartNumber}-${index}`}>
                    <Path path={path} />
                    {path &&
                      path.path_type &&
                      path.path_type.includes("TRANSPORTATION") && (
                        (() => {
                          const destinationPosition = convertPathPoitToLatLng(
                            path.path.points.at(-1)
                          );
                          if (!toLatLngPair(destinationPosition)) return null;
                          return (
                            <Marker
                              icon={L.divIcon({
                                className: "my-custom-pin",
                                html: `<div>${destinationIcon}</div>`,
                              })}
                              position={destinationPosition}
                            />
                          );
                        })()
                      )}
                  </React.Fragment>
                );
                })
            )
            : null}

          {/* Cart current location marker (supports [lat,lng] array or {lat,lng} object) */}
          {(() => {
            let latLng = null;
            if (props.cartLocation) {
              if (Array.isArray(props.cartLocation) && props.cartLocation.length >= 2) {
                const [lat, lng] = props.cartLocation;
                if (!Number.isNaN(parseFloat(lat)) && !Number.isNaN(parseFloat(lng))) {
                  latLng = { lat: parseFloat(lat), lng: parseFloat(lng) };
                }
              } else if (typeof props.cartLocation === 'object' && props.cartLocation.lat != null && props.cartLocation.lng != null) {
                const { lat, lng } = props.cartLocation;
                if (!Number.isNaN(parseFloat(lat)) && !Number.isNaN(parseFloat(lng))) {
                  latLng = { lat: parseFloat(lat), lng: parseFloat(lng) };
                }
              }
            }
            if (!latLng) return null;
            return (
              <Marker
                position={[latLng.lat, latLng.lng]}
                icon={L.divIcon({
                  className: 'my-custom-pin',
                  html: generateIconHtmlContent('C','', 'yellow')
                })}
              />
            );
          })()}

          {/* Reverse target marker (UI only) */}
          {(() => {
            const rp = props.reversePoint;
            if (!rp || rp.lat == null || rp.lng == null) return null;
            const lat = Number(rp.lat);
            const lng = Number(rp.lng);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
            return (
              <Marker
                position={[lat, lng]}
                icon={L.divIcon({
                  className: "my-custom-pin",
                  html: `<div style="width:14px;height:14px;border-radius:50%;background:#009ddb;border:2px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.25)"></div>`,
                  iconAnchor: [7, 7],
                })}
              />
            );
          })()}




        </MapContainer>
        </>
      ) : (
        <>
          {console.log('MapContainer NOT rendered - conditions not met:', {
            validCoordinates: validCoordinates,
            map_default_point: map_default_point,
            map_coords_buttom_left: map_coords_buttom_left,
            map_coords_top_right: map_coords_top_right,
            hasMapData: !!mapData,
            mapDataKeys: mapData?.data ? Object.keys(mapData.data) : 'none'
          })}
          <div style={{
            padding: '20px',
            textAlign: 'center',
            margin: '20px'
          }}>
            {mapLoadState === "loading" ? (
              <h3>Map Loading...</h3>
            ) : (
              <>
                <Alert severity="error" variant="outlined">
                  {mappingLoadError ||
                    "Map cannot be loaded because mapping data is missing or malformed."}
                </Alert>
                {mappingValidationWarnings.length > 0 && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    {mappingValidationWarnings[0]}
                  </Alert>
                )}
              </>
            )}
          </div>
        </>
      )}
    </React.Fragment>
  );
})

// {!isCalculatedZoom && <div onClick={()=> {setIsCalculatedZoom(true); ChangeView()} }  style={{  position:'relative' , top:'10%'}}> <FilterCenterFocusIcon  ></FilterCenterFocusIcon> </div>}{!isCalculatedZoom && <div onClick={()=> {setIsCalculatedZoom(true); ChangeView()} }  style={{  position:'relative' , top:'10%'}}> <FilterCenterFocusIcon  ></FilterCenterFocusIcon> </div>}
